import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { adminApi } from '../services/api'
import { Loader } from '../components/UI'
import { formatToman, formatDateTimeFa, toFaDigits } from '../utils/format'
import toast from 'react-hot-toast'

const STATUS_FLOW = [
  { value: 'PENDING', label: 'در انتظار تایید' },
  { value: 'CONFIRMED', label: 'تایید شده' },
  { value: 'PROCESSING', label: 'در حال پردازش' },
  { value: 'COMPLETED', label: 'تکمیل شده' }
]

const OrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const [order, setOrder] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState(false)

  useEffect(() => {
    if (!id) return
    adminApi
      .getOrder(id)
      .then((res) => setOrder(res.data.success ? res.data.data : null))
      .catch(() => setOrder(null))
      .finally(() => setLoading(false))
  }, [id])

  if (loading) return <div className="py-20 flex justify-center"><Loader /></div>
  if (!order) return <p className="py-20 text-center text-gray-500">سفارش یافت نشد</p>

  const changeStatus = async (status: string) => {
    setUpdating(true)
    try {
      const res = await adminApi.updateOrderStatus(order.id, status)
      if (res.data.success) {
        setOrder(res.data.data)
        toast.success('وضعیت به‌روزرسانی شد')
      } else {
        toast.error(res.data.message || 'خطا')
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'تغییر وضعیت مجاز نیست')
    } finally {
      setUpdating(false)
    }
  }

  return (
    <div className="space-y-6">
      <Link to="/orders" className="text-sm text-primary-600 font-bold hover:underline">← همه سفارش‌ها</Link>
      <div>
        <h1 className="text-2xl font-black text-gray-900" dir="ltr">{order.orderNumber}</h1>
        <p className="text-sm text-gray-500 mt-1">ثبت‌شده در {formatDateTimeFa(order.createdAt)}</p>
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        <div className="md:col-span-2 card overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 font-black">اقلام سفارش</div>
          <div className="divide-y divide-gray-50">
            {order.items.map((item: any) => (
              <div key={item.id} className="px-5 py-3 flex items-center justify-between gap-3 text-sm">
                <div>
                  <div className="font-bold text-gray-800">{item.product?.name || 'محصول'}</div>
                  <div className="text-xs text-gray-400">عیار {toFaDigits(item.product?.purity || 0)} — تعداد {toFaDigits(item.quantity)}</div>
                </div>
                <div className="text-left">
                  <div className="font-bold">{formatToman(item.totalPrice)}</div>
                  <div className="text-xs text-gray-400">واحد: {formatToman(item.unitPrice)}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="px-5 py-4 bg-gray-50 flex justify-between">
            <span className="font-bold">مبلغ کل</span>
            <span className="font-black text-primary-700">{formatToman(order.total)}</span>
          </div>
        </div>

        <div className="space-y-5">
          <div className="card p-5">
            <h2 className="font-black text-gray-900 mb-3">مشتری</h2>
            <p className="text-sm font-bold">{order.user?.firstName} {order.user?.lastName}</p>
            <p className="text-xs text-gray-500 mt-1" dir="ltr">{order.user?.email}</p>
            {order.user?.phone && <p className="text-xs text-gray-500 mt-1" dir="ltr">{order.user.phone}</p>}
          </div>

          {order.shippingAddress && (
            <div className="card p-5">
              <h2 className="font-black text-gray-900 mb-2">آدرس ارسال</h2>
              <p className="text-sm">{order.shippingAddress.province}، {order.shippingAddress.city}</p>
              <p className="text-xs text-gray-500 mt-1 leading-5">{order.shippingAddress.address}</p>
              <p className="text-xs text-gray-400 mt-1" dir="ltr">{order.shippingAddress.postalCode}</p>
            </div>
          )}

          {order.notes && (
            <div className="card p-5">
              <h2 className="font-black text-gray-900 mb-2">توضیحات</h2>
              <p className="text-sm text-gray-600">{order.notes}</p>
            </div>
          )}
        </div>
      </div>

      <div className="card p-5">
        <h2 className="font-black text-gray-900 mb-4">وضعیت سفارش</h2>
        <div className="flex flex-wrap gap-2">
          {STATUS_FLOW.map((s) => {
            const currentIdx = STATUS_FLOW.findIndex((x) => x.value === order.status)
            const sIdx = STATUS_FLOW.findIndex((x) => x.value === s.value)
            const done = sIdx < currentIdx
            const active = s.value === order.status
            return (
              <button
                key={s.value}
                disabled={updating || order.status === 'CANCELLED'}
                onClick={() => changeStatus(s.value)}
                className={`badge !py-2 !px-4 !text-sm border cursor-pointer disabled:cursor-not-allowed ${
                  active
                    ? 'bg-primary-600 text-white border-primary-600'
                    : done
                      ? 'bg-green-100 text-green-800 border-green-200'
                      : 'bg-white text-gray-500 border-gray-300 hover:border-primary-400'
                }`}
              >
                {s.label}
              </button>
            )
          })}
          {order.status === 'CANCELLED' && (
            <span className="badge bg-red-100 text-red-800 !py-2 !px-4 !text-sm">لغو شده</span>
          )}
        </div>
      </div>
    </div>
  )
}

export default OrderDetail